import React, { useEffect, useState, useRef, useCallback } from "react";
import "./CommentDanmaku.css";

/// 弾幕コメントの型定義
interface Comment {
  id: string;
  userId: string; // ユーザー固有ID（ブラウザ固有）
  username: string;
  text: string;
  timestamp: number;
  color?: string;
}

interface CommentDanmakuProps {
  comments: Comment[];
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface DanmakuItem extends Comment {
  x: number;
  y: number;
  speed: number;
  lane: number;
  startTime: number;
}

/// 動画上に流れるコメント弾幕コンポーネント
const CommentDanmaku: React.FC<CommentDanmakuProps> = ({
  comments,
  containerRef,
}) => {
  const [danmakuItems, setDanmakuItems] = useState<DanmakuItem[]>([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const animationRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(Date.now());
  const processedCommentsRef = useRef<Set<string>>(new Set());

  // プレイヤーコンテナのサイズを取得
  const getContainerSize = useCallback(() => {
    if (containerRef.current) {
      // YouTube iframe要素を直接参照して正確な位置とサイズを取得
      const iframe = containerRef.current.querySelector('iframe');
      if (iframe) {
        const rect = iframe.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        return { 
          width: rect.width, 
          height: rect.height,
          offsetX: rect.left - containerRect.left,
          offsetY: rect.top - containerRect.top
        };
      }
      
      // フォールバック: プレイヤーコンテナのサイズを使用
      const rect = containerRef.current.getBoundingClientRect();
      return { width: rect.width, height: rect.height, offsetX: 0, offsetY: 0 };
    }
    return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
  }, [containerRef]);

  // 画面幅の監視
  useEffect(() => {
    const handleResize = () => {
      const size = getContainerSize();
      setContainerSize(size);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getContainerSize]);

  // プレイヤーコンテナのサイズを監視
  useEffect(() => {
    const updateContainerSize = () => {
      const size = getContainerSize();
      setContainerSize(size);
    };

    const observer = new ResizeObserver(updateContainerSize);
    const currentContainer = containerRef.current;

    if (currentContainer) {
      observer.observe(currentContainer);
      updateContainerSize();
    }

    // プレイヤーの読み込みを待つ
    const checkForPlayer = setInterval(() => {
      if (containerRef.current) {
        updateContainerSize();
        clearInterval(checkForPlayer);
      }
    }, 500);

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
      clearInterval(checkForPlayer);
    };
  }, [containerRef, getContainerSize]);

  // 画面幅に基づく速度の動的設定
  const getSpeedByScreenWidth = (width: number): number => {
    if (width <= 480) {
      return 110; // スマホ: 遅い速度
    } else if (width <= 768) {
      return 150; // タブレット: 中程度の速度
    } else if (width <= 1024) {
      return 190; // 小型PC: やや速い速度
    } else {
      return 230; // 大画面PC: 速い速度
    }
  };

  // 動画の下10%には流れないよう、表示可能エリアを上90%に制限
  const availableHeight = containerSize.height * 0.9; // 上90%のエリアのみ使用

  const generateNonOverlappingY = (
    existingItems: DanmakuItem[],
    commentHeight: number,
    maxY: number,
  ): number => {
    const MAX_ATTEMPTS = 10;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      const y = Math.floor(Math.random() * Math.max(1, maxY));
      const overlaps = existingItems.some(
        (item) => Math.abs(item.y - y) < commentHeight,
      );
      if (!overlaps) {
        return y;
      }
    }
    // フォールバック: ランダムYをそのまま返す
    return Math.floor(Math.random() * Math.max(1, maxY));
  };

  /// 新しいコメントを弾幕に追加
  useEffect(() => {
    const newComments = comments.filter(
      (comment) => !processedCommentsRef.current.has(comment.id)
    );

    if (
      newComments.length > 0 &&
      containerSize.width > 0 &&
      containerSize.height > 0
    ) {
      const currentTime = Date.now();
      const MAX_DELAY = 2000; // 最大2秒のランダム遅延

      setDanmakuItems((prev) => {
        const commentHeight = 40;
        const maxY = availableHeight - commentHeight;

        const itemsSoFar: DanmakuItem[] = [...prev];

        const newDanmakuItems = newComments.map((comment) => {
          // ランダムなY座標を生成（重ならないように調整）
          const randomY = generateNonOverlappingY(
            itemsSoFar,
            commentHeight,
            maxY,
          );

          const speed = getSpeedByScreenWidth(containerSize.width);
          const startDelay = Math.random() * MAX_DELAY;

          processedCommentsRef.current.add(comment.id);

          const item: DanmakuItem = {
            ...comment,
            x: containerSize.width,
            y: randomY,
            speed,
            lane: 0,
            startTime: currentTime + startDelay,
          };

          itemsSoFar.push(item); // 次のコメントの重なりチェック用に追加

          return item;
        });

        return [...prev, ...newDanmakuItems];
      });
    }
  }, [
    comments,
    containerSize.width,
    containerSize.height,
    availableHeight,
  ]);

  /// アニメーションループ
  useEffect(() => {
    const animate = () => {
      const currentTime = Date.now();
      const deltaTime = currentTime - lastUpdateRef.current;
      lastUpdateRef.current = currentTime;

      setDanmakuItems((prev) => {
        return prev
          .map((item) => {
            // startTime 以前は移動させない
            if (currentTime < item.startTime) {
              return item;
            }
            return {
              ...item,
              x: item.x - (item.speed * deltaTime) / 1000,
            };
          })
          .filter((item) => item.x > -300); // 画面外に出たアイテムを削除
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  /// コンポーネントがアンマウントされるときのクリーンアップ
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // コンテナサイズが取得できない場合は何も表示しない
  if (containerSize.width === 0 || containerSize.height === 0) {
    return null;
  }

  return (
    <div
      className="comment-danmaku"
      style={{
        width: containerSize.width,
        height: containerSize.height * 0.9, // 上90%の高さのみ使用
        position: "absolute", // absoluteに変更
        top: containerSize.offsetY || 0, // iframe要素のオフセットを考慮
        left: containerSize.offsetX || 0, // iframe要素のオフセットを考慮
        overflow: "hidden", // はみ出したコメントを隠す
        zIndex: 900, // ヘッダー(z-index: 1000)より低く設定
        pointerEvents: "none",
        // backgroundColor: "rgba(255, 0, 0, 0.1)", // デバッグ用（赤い半透明背景）
      }}
    >
      {danmakuItems.map((item) => (
        <div
          key={item.id}
          className="danmaku-item"
          style={{
            transform: `translate(${item.x}px, ${item.y}px)`,
          }}
        >
          <span className="danmaku-text">{item.text}</span>
        </div>
      ))}
    </div>
  );
};

export default CommentDanmaku;
